import { createSlice } from "@reduxjs/toolkit";

import { RootState } from "@/redux";
import { AuthState } from "@/types/auth";
import { PAGE_SIZE_STORAGE_KEY } from "@/utils/preferredPageSize";

import { accountApi } from "@/pages/Private/redux/account/account.api";

import { hasSetPasswordAuthPayload } from "../pages/SetPassword/utils/set-password-response";

import { authApi } from "./auth.api";

const initialState: AuthState = {
	otpToken: null,
	token: localStorage.getItem("token") || null,
	user: null,
};

// UI preferences of this browser, not of the account: they outlive a logout.
const PRESERVED_LOCAL_STORAGE_KEYS = [
	"isSidebarPinned",
	"isSidebarOpen",
	PAGE_SIZE_STORAGE_KEY,
] as const;

const clearLocalStoragePreservingKeys = () => {
	const preserved = PRESERVED_LOCAL_STORAGE_KEYS.map(
		(key) => [key, localStorage.getItem(key)] as const
	);

	localStorage.clear();

	preserved.forEach(([key, value]) => {
		if (value !== null) {
			localStorage.setItem(key, value);
		}
	});
};

export const authSlice = createSlice({
	name: "auth",
	initialState,
	reducers: {
		resetAuth: (state) => {
			clearLocalStoragePreservingKeys();
			state.user = null;
			state.token = null;
			state.otpToken = null;
		},
		setAuth: (state, { payload }) => {
			const { otpToken, accessToken, token, user } = payload;
			const nextToken = accessToken ?? token;

			if (nextToken !== undefined) {
				state.token = nextToken;

				if (nextToken) {
					localStorage.setItem("token", nextToken);
				}
			}

			if (otpToken !== undefined) {
				state.otpToken = otpToken;
			}

			if (user !== undefined) {
				state.user = user;
			}
		},
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(accountApi.endpoints.changePassword.matchFulfilled, (state, { payload }) => {
				state.token = payload.accessToken;
				localStorage.setItem("token", payload.accessToken);
			})
			.addMatcher(authApi.endpoints.login.matchFulfilled, (state, { payload }) => {
				const { otpToken, accessToken, user } = payload;

				if (accessToken) {
					localStorage.setItem("token", accessToken);
					state.token = accessToken;
				}

				if (otpToken) {
					state.otpToken = otpToken;
				}

				state.user = user;
			})
			.addMatcher(authApi.endpoints.registerAccount.matchFulfilled, (state, { payload }) => {
				const { otpToken, accessToken, user } = payload;

				if (accessToken) {
					localStorage.setItem("token", accessToken);
					state.token = accessToken;
				}

				if (otpToken) {
					state.otpToken = otpToken;
				}

				state.user = user;
			})
			.addMatcher(authApi.endpoints.setPassword.matchFulfilled, (state, { payload }) => {
				if (!hasSetPasswordAuthPayload(payload)) {
					return;
				}

				const { otpToken, accessToken, user } = payload;

				localStorage.setItem("token", accessToken);
				state.token = accessToken;

				if (otpToken) {
					state.otpToken = otpToken;
				}

				state.user = user;
			})
			.addMatcher(
				authApi.endpoints.changePasswordWithToken.matchFulfilled,
				(state, { payload }) => {
					if (!hasSetPasswordAuthPayload(payload)) {
						return;
					}

					const { otpToken, accessToken, user } = payload;

					localStorage.setItem("token", accessToken);
					state.token = accessToken;

					if (otpToken) {
						state.otpToken = otpToken;
					}

					state.user = user;
				}
			)
			.addMatcher(authApi.endpoints.googleAuth.matchFulfilled, (state, { payload }) => {
				const { otpToken, accessToken, user } = payload;

				if (accessToken) {
					localStorage.setItem("token", accessToken);
					state.token = accessToken;
				}

				if (otpToken) {
					state.otpToken = otpToken;
				}

				state.user = user;
			})
			.addMatcher(authApi.endpoints.fetchMe.matchFulfilled, (state, { payload }) => {
				state.user = payload;
			})
			.addMatcher(accountApi.endpoints.updateMe.matchFulfilled, (state, { payload }) => {
				state.user = payload;
			})
			// The Profile step confirms the company (website/name) — without folding
			// that back in, the next onboarding steps keep rendering the company we
			// guessed from the signup email domain. Only the company is merged: the
			// onboarding response is built without roles/permissions, so replacing
			// the whole user would strip them.
			.addMatcher(
				accountApi.endpoints.saveOnboardingSettings.matchFulfilled,
				(state, { payload }) => {
					if (state.user && payload?.company) {
						state.user = { ...state.user, company: payload.company };
					}
				}
			)
			.addMatcher(authApi.endpoints.authTwoFactorCode.matchFulfilled, (state, { payload }) => {
				const { accessToken, user } = payload;

				if (accessToken) {
					localStorage.setItem("token", accessToken);
					state.token = accessToken;
					state.otpToken = null;
				}

				state.user = user;
			})
			.addMatcher(accountApi.endpoints.verifyTwoFactor.matchFulfilled, (state, { payload }) => {
				state.user = payload;
			})
			.addMatcher(accountApi.endpoints.disableTwoFactor.matchFulfilled, (state, { payload }) => {
				state.user = payload;
			});
	},
});

export const { resetAuth, setAuth } = authSlice.actions;
export const authReducer = authSlice.reducer;
export const authSelector = (state: RootState) => state[authSlice.name];
